import React, { useEffect, useMemo, useRef, useState } from "react";
import { CardDescription } from "./card";
import { Search, Check, ChevronDown } from "lucide-react";

function useOutsideClick(ref, onClickOutside) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside?.();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref, onClickOutside]);
}

/**
 * Props:
 *  - options: string[] | {label:string, value:string}[]
 *  - value?: string   (controlled)
 *  - onSelect?: (value: string) => void
 *  - label?: string
 *  - description?: string
 *  - placeholder?: string
 *  - disabled?: boolean
 *  - triggerClassName?: string
 *  - panelClassName?: string
 *  - searchWrapperClassName?: string
 *  - searchIconClassName?: string
 *  - searchInputClassName?: string
 *  - listClassName?: string
 *  - optionClassName?: string
 *  - emptyStateClassName?: string
 */
export default function SingleSelectDropdown({
  options = [],
  value,
  onSelect = () => {},
  label = "",
  description = "",
  placeholder = "Select specialty",
  disabled = false,
  triggerClassName,
  panelClassName,
  searchWrapperClassName,
  searchIconClassName,
  searchInputClassName,
  listClassName,
  optionClassName,
  emptyStateClassName,
}) {
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [innerValue, setInnerValue] = useState(undefined); // for uncontrolled mode

  const isControlled = value !== undefined && value !== null;
  const currentValue = isControlled ? value : innerValue;

  // Normalize options to [{label,value}]
  const normalized = useMemo(() => {
    const base = (Array.isArray(options) ? options : []).map((o) =>
      typeof o === "string" ? { label: o, value: o } : o
    );

    if (currentValue && !base.some((o) => o.value === currentValue)) {
      base.unshift({ label: currentValue, value: currentValue });
    }
    return base;
  }, [options, currentValue]);

  // Derive selected label for button
  const selectedLabel = useMemo(() => {
    const found = normalized.find((o) => o.value === currentValue);
    return found ? found.label : "";
  }, [normalized, currentValue]);

  useOutsideClick(dropdownRef, () => setOpen(false));

  useEffect(() => {
    if (isControlled) return;
  }, [isControlled, value]);

  const handleSelect = (opt) => {
    const next = typeof opt === "string" ? opt : opt?.value;
    if (!isControlled) setInnerValue(next);
    onSelect(next);
    setOpen(false);
    setSearchTerm("");
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [normalized, searchTerm]);

  useEffect(() => {
    if (open) {
      const selectedIdx = filtered.findIndex((o) => o.value === currentValue);
      setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : (filtered.length > 0 ? 0 : -1));
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setHighlightedIndex(filtered.length > 0 ? 0 : -1);
    }
  }, [filtered, open]);

  useEffect(() => {
    if (open && listRef.current && highlightedIndex >= 0) {
      const item = listRef.current.children[highlightedIndex];
      if (item && typeof item.scrollIntoView === "function") {
        item.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, open]);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length > 0) {
        setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length > 0) {
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        handleSelect(filtered[highlightedIndex]);
        triggerRef.current?.focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const triggerClasses = triggerClassName
    ? [triggerClassName, disabled ? "cursor-not-allowed opacity-60" : ""].filter(Boolean).join(" ")
    : [
        "w-full rounded-md border border-gray-300 px-3 py-2 text-left text-sm",
        disabled ? "cursor-not-allowed bg-gray-100 text-gray-400" : "cursor-pointer bg-white text-gray-900",
      ].join(" ");

  const panelClasses =
    panelClassName ||
    "absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-gray-300 bg-white text-gray-900 shadow-lg";
  const searchWrapperClasses =
    searchWrapperClassName || "flex items-center border-b p-2";
  const searchIconClasses = searchIconClassName || "mr-2 h-4 w-4 text-gray-500";
  const searchInputClasses =
    searchInputClassName || "w-full text-sm outline-none placeholder:text-gray-400";
  const listClasses = listClassName || "max-h-60 overflow-y-auto py-1";
  const emptyClasses = emptyStateClassName || "px-3 py-2 text-sm text-gray-500";

  return (
    <div className={`space-y-1 ${open ? "relative z-50" : ""}`} ref={dropdownRef}>
      {label ? <h2>{label}</h2> : null}
      {description ? <CardDescription className="text-gray-600">{description}</CardDescription> : null}

      <div className={`relative ${open ? "z-50" : ""}`}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={handleTriggerKeyDown}
          className={`${triggerClasses} flex items-center justify-between`}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-open={open}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 text-slate-400 ${open ? "rotate-180" : ""}`} />
        </button>

        {open && !disabled && (
          <div className={panelClasses}>
            <div className={searchWrapperClasses}>
              <Search className={searchIconClasses} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                className={searchInputClasses}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleInputKeyDown}
                autoFocus
              />
            </div>

            <ul ref={listRef} role="listbox" className={listClasses}>
              {filtered.length === 0 ? (
                <li className={emptyClasses}>No results</li>
              ) : (
                filtered.map((o, idx) => {
                  const isSelected = o.value === currentValue;
                  const isHighlighted = idx === highlightedIndex;
                  const itemClassName = optionClassName
                    ? [optionClassName, "cursor-pointer"].join(" ")
                    : [
                        "px-3 py-2 text-sm cursor-pointer",
                        isSelected ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100",
                      ].join(" ");

                  const highlightClass = isHighlighted
                    ? isSelected
                      ? "!bg-blue-100 !text-blue-700 font-semibold"
                      : "!bg-blue-50 !text-blue-600 font-medium"
                    : "";

                  return (
                    <li
                      key={o.value}
                      role="option"
                      aria-selected={isSelected}
                      data-selected={isSelected ? "true" : "false"}
                      data-highlighted={isHighlighted ? "true" : "false"}
                      className={`${itemClassName} flex items-center justify-between ${highlightClass}`}
                      onClick={() => {
                        handleSelect(o);
                        triggerRef.current?.focus();
                      }}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                    >
                      <span>{o.label}</span>
                      {isSelected && <Check className="h-4 w-4 text-blue-600 shrink-0 ml-2" />}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

